import React from 'react';
import { AlertTriangle, Check, Download, FileSpreadsheet, Paperclip, Plus, Users } from 'lucide-react';
import { InlineLoadingState } from '../../../../components/LoadingSpinner';
import { AppSelect } from '../../../../components/AppSelect';
import { DatePicker } from '../../../../components/DatePicker';
import { SettingsCard } from '../../components/SettingsCard';
import { SettingsStatusChip } from '../../components/SettingsStatusChip';
import { SettingsToggle } from '../../components/SettingsToggle';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
  SETTINGS_TABLE_HEADER,
} from '../../utils/settingsSectionStyles';
import { refreshDatevStatus } from '../../../datev/useDatevStatus';
import { DatevExportModal } from '../../../datev/DatevExportModal';
import { loadDatevOverview, type DatevOverview } from '../../../datev/datevOverview';
import {
  IntegrationDetailLayout,
  SettingsField,
  SettingsGroup,
  UnsavedChangesBar,
  type IntegrationState,
} from '../integrations/integrationUi';

export type DatevSettings = {
  enabled: boolean;
  berater_nr: number | null;
  mandant_nr: number | null;
  chart: '03' | '04';
  account_length: number;
  fiscal_year_start_month: number;
  debitor_from: number | null;
  debitor_to: number | null;
  kreditor_from: number | null;
  kreditor_to: number | null;
  transfer_from: string | null;
  lock_batches: boolean;
  instruction_accepted_at: string | null;
  instructionCurrent: boolean;
  missing: string[];
};

type Mapping = {
  direction: 'outgoing' | 'incoming';
  taxCase: string;
  label: string;
  account: string;
  buKey: string | null;
  isDefault: boolean;
};

type CostCenter = { id: string; code: string; name: string; active: boolean };

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export const DATEV_INSTRUCTION_TEXT =
  'Wir weisen Meisterpfad an, unsere Rechnungen, Belege und die Stammdaten unserer Kunden und Lieferanten im DATEV-Format für unsere Steuerkanzlei bereitzustellen. Ob die Daten in DATEV vollständig und richtig angekommen sind, prüfen wir bzw. unsere Kanzlei selbst.';

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const d = await res.json().catch(() => null);
  if (!res.ok) throw new Error(d?.error || 'Anfrage fehlgeschlagen.');
  return d as T;
}

function ErrorBox({ text }: { text: string | null }) {
  if (!text) return null;
  return <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{text}</div>;
}

type Draft = Record<
  'berater_nr' | 'mandant_nr' | 'debitor_from' | 'debitor_to' | 'kreditor_from' | 'kreditor_to' | 'transfer_from',
  string
> & { chart: '03' | '04'; account_length: number; fiscal_year_start_month: number; lock_batches: boolean };

function draftFrom(settings: DatevSettings): Draft {
  const str = (value: number | string | null) => (value == null ? '' : String(value));
  return {
    berater_nr: str(settings.berater_nr),
    mandant_nr: str(settings.mandant_nr),
    debitor_from: str(settings.debitor_from),
    debitor_to: str(settings.debitor_to),
    kreditor_from: str(settings.kreditor_from),
    kreditor_to: str(settings.kreditor_to),
    transfer_from: str(settings.transfer_from),
    chart: settings.chart,
    account_length: settings.account_length,
    fiscal_year_start_month: settings.fiscal_year_start_month,
    lock_batches: settings.lock_batches,
  };
}

function defaultRanges(length: number) {
  const base = 10 ** length;
  return { debitor_from: String(base), debitor_to: String(7 * base - 1), kreditor_from: String(7 * base), kreditor_to: String(10 * base - 1) };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

type Batch = { id: string; createdAt: string; periodFrom: string; periodTo: string; status: 'created' | 'transferred' | 'reverted'; invoiceCount: number };

const BATCH_STATUS: Record<Batch['status'], { label: string; className: string }> = {
  created: { label: 'Erzeugt', className: 'text-app-text-muted' },
  transferred: { label: 'Übertragen', className: 'text-emerald-700' },
  reverted: { label: 'Zurückgenommen', className: 'text-app-text-subtle' },
};

const TABS = [
  { id: 'setup', label: 'Einrichtung' },
  { id: 'accounts', label: 'Kontenzuordnung' },
  { id: 'cost_centers', label: 'Kostenstellen' },
  { id: 'exports', label: 'Exporte' },
];

const CHART_LABEL: Record<DatevSettings['chart'], string> = { '03': 'SKR03', '04': 'SKR04' };

const DESCRIPTION = 'Rechnungen, Belege, Debitoren und Kreditoren als DATEV-Export für deine Steuerkanzlei.';

export function DatevSettingsPage({ onBack }: { onBack: () => void }) {
  const [settings, setSettings] = React.useState<DatevSettings | null>(null);
  const [mappings, setMappings] = React.useState<Mapping[]>([]);
  const [costCenters, setCostCenters] = React.useState<CostCenter[]>([]);
  const [structureLocked, setStructureLocked] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState('setup');
  const [wizardOpen, setWizardOpen] = React.useState(false);
  const [batches, setBatches] = React.useState<Batch[] | null>(null);
  const [overview, setOverview] = React.useState<DatevOverview | null>(null);
  const [exportOpen, setExportOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoadError(null);
    try {
      const d = await api<{ settings: DatevSettings; mappings: Mapping[]; costCenters: CostCenter[]; structureLocked: boolean }>('/api/settings/datev');
      setSettings(d.settings);
      setMappings(d.mappings);
      setCostCenters(d.costCenters);
      setStructureLocked(d.structureLocked);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const ready = Boolean(settings?.enabled && settings.missing.length === 0);
  const transferFrom = settings?.transfer_from ?? null;

  const loadExports = React.useCallback(async () => {
    try {
      const [list, next] = await Promise.all([api<{ batches: Batch[] }>('/api/datev/exports'), loadDatevOverview(transferFrom)]);
      setBatches(list.batches);
      setOverview(next);
    } catch {
      setBatches([]);
      setOverview(null);
    }
  }, [transferFrom]);

  React.useEffect(() => {
    if (ready) void loadExports();
  }, [ready, loadExports]);

  const state: IntegrationState | null = !settings
    ? null
    : !settings.enabled
      ? 'off'
      : settings.missing.length > 0
        ? 'incomplete'
        : 'connected';
  const facts = settings?.enabled
    ? [
        CHART_LABEL[settings.chart],
        settings.berater_nr && settings.mandant_nr ? `Berater ${settings.berater_nr} / Mandant ${settings.mandant_nr}` : null,
      ].filter(Boolean)
    : [];

  // Nicht verbunden: „Einrichten“ steht nur auf der Einrichtungsseite, nicht doppelt im Kopf.
  let action: React.ReactNode = null;
  if (ready) {
    action = (
      <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={() => setExportOpen(true)}>
        <Download className="h-4 w-4" />
        Export erstellen
      </button>
    );
  }

  return (
    <IntegrationDetailLayout
      mark="DATEV"
      title="DATEV"
      state={state}
      description={[DESCRIPTION, ...facts].join(' · ')}
      onBack={onBack}
      action={action}
      tabs={settings?.enabled ? { items: TABS, active: tab, onChange: setTab } : undefined}
    >
      {loadError ? <div className="pt-6"><ErrorBox text={loadError} /></div> : null}
      {!settings && !loadError ? <InlineLoadingState label="DATEV-Einstellungen werden geladen …" /> : null}
      {settings && !settings.enabled && wizardOpen ? (
        <SetupWizard
          settings={settings}
          onCancel={() => setWizardOpen(false)}
          onDone={(next) => {
            setSettings(next);
            setWizardOpen(false);
            setTab('setup');
          }}
        />
      ) : null}
      {settings && !settings.enabled && !wizardOpen ? <SetupLanding settings={settings} onStart={() => setWizardOpen(true)} /> : null}
      {settings?.enabled && tab === 'setup' ? (
        <>
          {settings.missing.length > 0 ? <MissingHint missing={settings.missing} /> : null}
          <AccountingForm settings={settings} structureLocked={structureLocked} onSaved={setSettings} />
        </>
      ) : null}
      {settings?.enabled && tab === 'accounts' ? <MappingCard chart={settings.chart} mappings={mappings} onChange={setMappings} /> : null}
      {settings?.enabled && tab === 'cost_centers' ? <CostCenterCard costCenters={costCenters} onChange={setCostCenters} /> : null}
      {settings?.enabled && tab === 'exports' ? (
        <ExportsTab ready={ready} overview={overview} batches={ready ? batches : []} onCreate={() => setExportOpen(true)} />
      ) : null}
      <DatevExportModal open={exportOpen} onClose={() => setExportOpen(false)} onExported={() => void loadExports()} />
    </IntegrationDetailLayout>
  );
}

/** Nicht verbunden: was die Integration macht, statt leerer Formulare. */
function SetupLanding({ settings, onStart }: { settings: DatevSettings; onStart: () => void }) {
  const points: Array<{ icon: React.ReactNode; title: string; text: string }> = [
    { icon: <FileSpreadsheet className="h-5 w-5" />, title: 'Buchungsstapel', text: 'Ausgangs- und Eingangsrechnungen im DATEV-Format, mit Kostenstelle und Projekt.' },
    { icon: <Users className="h-5 w-5" />, title: 'Debitoren und Kreditoren', text: 'Kunden und Lieferanten bekommen beim ersten Export ihre Kontonummer.' },
    { icon: <Paperclip className="h-5 w-5" />, title: 'Belege', text: 'Die Rechnungen liegen als PDF bzw. E-Rechnung im selben ZIP.' },
  ];
  return (
    <div className="rounded-2xl border border-app-border bg-app-surface p-6 md:p-8">
      <h2 className="text-lg font-semibold text-app-text">Rechnungen ohne Abtippen an die Kanzlei</h2>
      <p className="mt-1 max-w-xl text-sm text-app-text-muted">
        Meisterpfad erzeugt den Export, du lädst ihn herunter oder schickst ihn der Kanzlei. Die Einrichtung dauert etwa zwei Minuten.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {points.map((point) => (
          <div key={point.title} className="rounded-xl bg-app-surface-muted p-4">
            <div className="text-pipedrive-blue">{point.icon}</div>
            <p className="mt-3 text-sm font-semibold text-app-text">{point.title}</p>
            <p className="mt-1 text-sm leading-relaxed text-app-text-muted">{point.text}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={onStart}>
          Einrichten
        </button>
        <span className="text-sm text-app-text-muted">
          {settings.berater_nr ? 'Deine bisherigen Angaben sind noch da.' : 'Du brauchst Berater- und Mandantennummer von deiner Kanzlei.'}
        </span>
      </div>
    </div>
  );
}

const WIZARD_STEPS = ['Weisung', 'Kanzlei', 'Übergabe'] as const;

function SetupWizard({
  settings,
  onCancel,
  onDone,
}: {
  settings: DatevSettings;
  onCancel: () => void;
  onDone: (next: DatevSettings) => void;
}) {
  const [step, setStep] = React.useState(0);
  const [accepted, setAccepted] = React.useState(settings.instructionCurrent);
  const [berater, setBerater] = React.useState(settings.berater_nr ? String(settings.berater_nr) : '');
  const [mandant, setMandant] = React.useState(settings.mandant_nr ? String(settings.mandant_nr) : '');
  const [chart, setChart] = React.useState<'03' | '04'>(settings.chart);
  const [accountLength, setAccountLength] = React.useState(settings.account_length);
  const [transferFrom, setTransferFrom] = React.useState(settings.transfer_from ?? '');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const canNext = step === 0 ? accepted : step === 1 ? Boolean(berater && mandant) : Boolean(transferFrom);

  const finish = async () => {
    setSaving(true);
    setError(null);
    try {
      // Vorhandene Bereiche behalten; leere oder unpassende bekommen den Standard zur Sachkontenlänge.
      const defaults = defaultRanges(accountLength);
      const keep = settings.account_length === accountLength;
      const range = (key: keyof typeof defaults) => (keep && settings[key] ? settings[key] : Number(defaults[key]));
      const d = await api<{ settings: DatevSettings }>('/api/settings/datev', {
        method: 'PUT',
        body: JSON.stringify({
          berater_nr: Number(berater),
          mandant_nr: Number(mandant),
          chart,
          account_length: accountLength,
          transfer_from: transferFrom,
          debitor_from: range('debitor_from'),
          debitor_to: range('debitor_to'),
          kreditor_from: range('kreditor_from'),
          kreditor_to: range('kreditor_to'),
          ...(settings.instructionCurrent ? {} : { accept_instruction: true }),
          enabled: true,
        }),
      });
      await refreshDatevStatus();
      onDone(d.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-app-border bg-app-surface">
      {/* Schritte links, Mitte, rechts; dazwischen Linien, die sich mit dem Fortschritt füllen. */}
      <ol className="flex items-center gap-3 border-b border-app-border px-6 py-4 md:px-8">
        {WIZARD_STEPS.map((label, index) => (
          <React.Fragment key={label}>
            {index > 0 ? <li aria-hidden className={`h-px min-w-6 flex-1 ${index <= step ? 'bg-emerald-500' : 'bg-app-border'}`} /> : null}
            <li className={`flex shrink-0 items-center gap-2 text-sm ${index === step ? 'font-semibold text-app-text' : 'text-app-text-muted'}`}>
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  index < step ? 'bg-emerald-500 text-white' : index === step ? 'bg-pipedrive-blue text-white' : 'bg-app-surface-muted text-app-text-muted'
                }`}
              >
                {index < step ? <Check className="h-3.5 w-3.5" /> : index + 1}
              </span>
              <span>{label}</span>
            </li>
          </React.Fragment>
        ))}
      </ol>

      <div className="space-y-5 p-6 md:p-8">
        {step === 0 ? (
          <>
            <div>
              <h2 className="text-base font-semibold text-app-text">Weisung zur Übermittlung</h2>
              <p className="mt-1 text-sm text-app-text-muted">Datenschutzrechtlich brauchen wir deine Weisung, bevor Daten für die Kanzlei aufbereitet werden.</p>
            </div>
            <p className="rounded-xl bg-app-surface-muted p-4 text-sm leading-relaxed text-app-text">{DATEV_INSTRUCTION_TEXT}</p>
            {settings.instructionCurrent ? (
              <p className="text-sm text-app-text-muted">Bereits bestätigt am {formatDate(settings.instruction_accepted_at)}.</p>
            ) : (
              <label className="flex items-start gap-2.5 text-sm text-app-text">
                <input type="checkbox" className="mt-0.5" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                <span>Ich bestätige diese Weisung für unser Unternehmen.</span>
              </label>
            )}
          </>
        ) : null}

        {step === 1 ? (
          <>
            <div>
              <h2 className="text-base font-semibold text-app-text">Kanzlei und Mandant</h2>
              <p className="mt-1 text-sm text-app-text-muted">Diese Angaben bekommst du von deiner Steuerkanzlei.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <WizardField label="Beraternummer">
                <input
                  inputMode="numeric"
                  value={berater}
                  onChange={(e) => setBerater(e.target.value.replace(/[^\d]/g, '').slice(0, 7))}
                  className={SETTINGS_INPUT_CLASS}
                  placeholder="z. B. 29098"
                />
              </WizardField>
              <WizardField label="Mandantennummer">
                <input
                  inputMode="numeric"
                  value={mandant}
                  onChange={(e) => setMandant(e.target.value.replace(/[^\d]/g, '').slice(0, 5))}
                  className={SETTINGS_INPUT_CLASS}
                  placeholder="z. B. 55003"
                />
              </WizardField>
              <WizardField label="Kontenrahmen">
                <SegmentedChart value={chart} onChange={setChart} />
              </WizardField>
              <WizardField label="Sachkontenlänge" hint="Meist 4. Im Zweifel die Kanzlei fragen.">
                <AppSelect
                  value={String(accountLength)}
                  onChange={(v) => setAccountLength(Number(v))}
                  options={[4, 5, 6, 7, 8].map((n) => ({ value: String(n), label: `${n} Stellen` }))}
                  triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                  aria-label="Sachkontenlänge"
                />
              </WizardField>
            </div>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <div>
              <h2 className="text-base font-semibold text-app-text">Ab wann übergeben?</h2>
              <p className="mt-1 text-sm text-app-text-muted">
                Nur Rechnungen ab diesem Rechnungsdatum gehen an DATEV. Alles davor hat die Kanzlei schon gebucht. Meist der erste Tag des laufenden Monats.
              </p>
            </div>
            <WizardField label="Übergabe ab">
              <DatePicker value={transferFrom} onChange={setTransferFrom} className="w-44" />
            </WizardField>
          </>
        ) : null}

        <ErrorBox text={error} />
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-app-border px-6 py-4 md:px-8">
        <button type="button" className={SETTINGS_BTN_OUTLINE} disabled={saving} onClick={() => (step === 0 ? onCancel() : setStep(step - 1))}>
          {step === 0 ? 'Abbrechen' : 'Zurück'}
        </button>
        {step < WIZARD_STEPS.length - 1 ? (
          <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={!canNext} onClick={() => setStep(step + 1)}>
            Weiter
          </button>
        ) : (
          <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={!canNext || saving} onClick={() => void finish()}>
            {saving ? 'Wird aktiviert …' : 'DATEV aktivieren'}
          </button>
        )}
      </div>
    </div>
  );
}

function WizardField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-sm font-medium text-app-text">{label}</p>
      {children}
      {hint ? <p className="mt-1 text-xs text-app-text-muted">{hint}</p> : null}
    </div>
  );
}

function SegmentedChart({ value, onChange, disabled = false }: { value: '03' | '04'; onChange: (next: '03' | '04') => void; disabled?: boolean }) {
  return (
    <div role="radiogroup" aria-label="Kontenrahmen" className="inline-flex rounded-lg border border-app-border bg-app-surface-muted p-0.5">
      {(['03', '04'] as const).map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          disabled={disabled}
          onClick={() => onChange(option)}
          className={`rounded-md px-3.5 py-1.5 text-sm font-medium disabled:cursor-not-allowed ${
            value === option ? 'bg-app-surface text-app-text shadow-sm' : 'text-app-text-muted hover:text-app-text'
          }`}
        >
          SKR{option}
        </button>
      ))}
    </div>
  );
}

function MissingHint({ missing }: { missing: string[] }) {
  return (
    <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        <span className="font-semibold">
          Noch {missing.length} {missing.length === 1 ? 'Angabe' : 'Angaben'} bis zum ersten Export:
        </span>{' '}
        {missing.join(', ')}.
      </p>
    </div>
  );
}

/** Einrichtung: Felder direkt bearbeitbar, gespeichert wird über die Leiste unten. */
function AccountingForm({
  settings,
  structureLocked,
  onSaved,
}: {
  settings: DatevSettings;
  structureLocked: boolean;
  onSaved: (next: DatevSettings) => void;
}) {
  const initial = React.useMemo(() => draftFrom(settings), [settings]);
  const [values, setValues] = React.useState<Draft>(initial);
  const [saving, setSaving] = React.useState(false);
  const [toggling, setToggling] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => setValues(initial), [initial]);
  const dirty = JSON.stringify(values) !== JSON.stringify(initial);

  const set = (patch: Partial<Draft>) => setValues((current) => ({ ...current, ...patch }));
  const setLength = (length: number) => {
    const current = defaultRanges(values.account_length);
    const rangesAreDefault = (['debitor_from', 'debitor_to', 'kreditor_from', 'kreditor_to'] as const).every(
      (key) => !values[key] || values[key] === current[key],
    );
    set({ account_length: length, ...(rangesAreDefault ? defaultRanges(length) : {}) });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const toNumber = (value: string) => (value.trim() ? Number(value.trim()) : null);
      const d = await api<{ settings: DatevSettings }>('/api/settings/datev', {
        method: 'PUT',
        body: JSON.stringify({
          berater_nr: toNumber(values.berater_nr),
          mandant_nr: toNumber(values.mandant_nr),
          chart: values.chart,
          account_length: values.account_length,
          fiscal_year_start_month: values.fiscal_year_start_month,
          debitor_from: toNumber(values.debitor_from),
          debitor_to: toNumber(values.debitor_to),
          kreditor_from: toNumber(values.kreditor_from),
          kreditor_to: toNumber(values.kreditor_to),
          transfer_from: values.transfer_from || null,
          lock_batches: values.lock_batches,
        }),
      });
      onSaved(d.settings);
      await refreshDatevStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    setToggling(true);
    setError(null);
    try {
      const d = await api<{ settings: DatevSettings }>('/api/settings/datev', { method: 'PUT', body: JSON.stringify({ enabled: false }) });
      onSaved(d.settings);
      await refreshDatevStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setToggling(false);
    }
  };

  const numberInput = (key: keyof Draft, label: string, width = 'w-32') => (
    <input
      inputMode="numeric"
      aria-label={label}
      value={String(values[key])}
      disabled={saving}
      onChange={(e) => set({ [key]: e.target.value.replace(/[^\d]/g, '') } as Partial<Draft>)}
      className={`${SETTINGS_INPUT_CLASS} ${width} text-right tabular-nums`}
    />
  );

  return (
    <div>
      <SettingsGroup title="Kanzlei" description="Diese Angaben bekommst du von deiner Steuerkanzlei.">
        <SettingsField label="Beraternummer">{numberInput('berater_nr', 'Beraternummer')}</SettingsField>
        <SettingsField label="Mandantennummer">{numberInput('mandant_nr', 'Mandantennummer')}</SettingsField>
      </SettingsGroup>

      <SettingsGroup
        title="Buchhaltung"
        description={structureLocked ? 'Kontenrahmen und Sachkontenlänge sind nach dem ersten Export fest.' : 'Wie die Kanzlei euren Mandanten führt.'}
      >
        <SettingsField label="Kontenrahmen">
          <SegmentedChart value={values.chart} disabled={structureLocked || saving} onChange={(chart) => set({ chart })} />
        </SettingsField>
        <SettingsField label="Sachkontenlänge" hint="Personenkonten haben eine Stelle mehr.">
          {structureLocked ? (
            <span className="text-sm text-app-text">{values.account_length} Stellen</span>
          ) : (
            <AppSelect
              value={String(values.account_length)}
              onChange={(v) => setLength(Number(v))}
              options={[4, 5, 6, 7, 8].map((n) => ({ value: String(n), label: `${n} Stellen` }))}
              triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
              className="w-32"
              aria-label="Sachkontenlänge"
            />
          )}
        </SettingsField>
        <SettingsField label="Wirtschaftsjahr beginnt im">
          <AppSelect
            value={String(values.fiscal_year_start_month)}
            onChange={(v) => set({ fiscal_year_start_month: Number(v) })}
            options={MONTHS.map((label, i) => ({ value: String(i + 1), label }))}
            triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
            className="w-40"
            aria-label="Beginn des Wirtschaftsjahres"
          />
        </SettingsField>
      </SettingsGroup>

      <SettingsGroup title="Personenkonten" description="Meisterpfad vergibt die Nummern beim ersten Export aus diesen Bereichen.">
        <SettingsField label="Debitoren" hint="Kunden">
          {numberInput('debitor_from', 'Debitoren von', 'w-28')}
          <span className="text-sm text-app-text-muted">bis</span>
          {numberInput('debitor_to', 'Debitoren bis', 'w-28')}
        </SettingsField>
        <SettingsField label="Kreditoren" hint="Lieferanten">
          {numberInput('kreditor_from', 'Kreditoren von', 'w-28')}
          <span className="text-sm text-app-text-muted">bis</span>
          {numberInput('kreditor_to', 'Kreditoren bis', 'w-28')}
        </SettingsField>
      </SettingsGroup>

      <SettingsGroup title="Übergabe" description="Was an DATEV geht und in welcher Form.">
        <SettingsField label="Übergabe ab" hint="Nur Rechnungen ab diesem Rechnungsdatum.">
          <DatePicker value={values.transfer_from} disabled={saving} onChange={(transfer_from) => set({ transfer_from })} className="w-44" />
        </SettingsField>
        <SettingsField label="Festschreiben" hint="Stapel festgeschrieben übergeben. Nur nach Absprache mit der Kanzlei.">
          <SettingsToggle active={values.lock_batches} disabled={saving} label="Festschreiben" onClick={() => set({ lock_batches: !values.lock_batches })} />
        </SettingsField>
      </SettingsGroup>

      <SettingsGroup title="Integration" description="Beim Deaktivieren bleiben Einstellungen und bisherige Exporte erhalten.">
        <SettingsField label="DATEV aktiv" hint={`Weisung bestätigt am ${formatDate(settings.instruction_accepted_at)}.`}>
          <SettingsToggle active disabled={toggling || saving} label="DATEV aktiv" onClick={() => void deactivate()} />
        </SettingsField>
      </SettingsGroup>

      <UnsavedChangesBar
        open={dirty || Boolean(error)}
        saving={saving}
        error={error}
        onSave={() => void save()}
        onDiscard={() => {
          setValues(initial);
          setError(null);
        }}
      />
    </div>
  );
}

function ExportsTab({
  ready,
  overview,
  batches,
  onCreate,
}: {
  ready: boolean;
  overview: DatevOverview | null;
  batches: Batch[] | null;
  onCreate: () => void;
}) {
  const lastExport = batches?.find((batch) => batch.status !== 'reverted');
  const stats: Array<{ label: string; value: React.ReactNode; tone?: string }> = [
    { label: 'Bereit zum Export', value: overview ? overview.ready : '…' },
    { label: 'Mit Fehlern', value: overview ? overview.failing : '…', tone: overview && overview.failing > 0 ? 'text-red-700' : undefined },
    { label: 'Letzter Export', value: lastExport ? formatDate(lastExport.createdAt) : '—' },
  ];
  return (
    <div className="pt-6">
      {ready ? (
        <div className="mb-6 grid gap-3 sm:grid-cols-3">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-xl border border-app-border bg-app-surface px-5 py-4">
              <p className="text-xs font-medium text-app-text-muted">{stat.label}</p>
              <p className={`mt-1 text-2xl font-semibold tabular-nums ${stat.tone ?? 'text-app-text'}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      ) : null}
      <ExportsCard batches={batches} canExport={ready} onCreate={onCreate} />
      {ready ? <p className="mt-3 text-xs text-app-text-muted">Bitte prüfe das Importprotokoll in DATEV nach jeder Übergabe.</p> : null}
    </div>
  );
}

function ExportsCard({ batches, canExport, onCreate }: { batches: Batch[] | null; canExport: boolean; onCreate: () => void }) {
  return (
    <SettingsCard
      title="Exporte"
      description="Jeder Export ist ein ZIP mit Buchungsstapel, Stammdaten und Belegen. Übertragen und Zurücknehmen geht im Export-Dialog."
      actions={
        canExport ? (
          <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={onCreate}>
            Export-Dialog öffnen
          </button>
        ) : null
      }
    >
      {!canExport ? <p className="text-sm text-app-text-muted">Exporte sind möglich, sobald die Einrichtung vollständig ist.</p> : null}
      {canExport && !batches ? <InlineLoadingState label="Exporte werden geladen …" /> : null}
      {canExport && batches && batches.length === 0 ? <p className="text-sm text-app-text-muted">Noch kein Export.</p> : null}
      {batches && batches.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className={`${SETTINGS_TABLE_HEADER} text-left`}>
                <th className="pb-2 font-medium">Zeitraum</th>
                <th className="pb-2 font-medium">Erstellt</th>
                <th className="pb-2 text-right font-medium">Rechnungen</th>
                <th className="pb-2 pl-4 font-medium">Status</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-app-border">
              {batches.map((batch) => (
                <tr key={batch.id}>
                  <td className="py-2.5 text-app-text">
                    {formatDate(batch.periodFrom)} – {formatDate(batch.periodTo)}
                  </td>
                  <td className="py-2.5 text-app-text-muted">{formatDate(batch.createdAt)}</td>
                  <td className="py-2.5 text-right text-app-text">{batch.invoiceCount}</td>
                  <td className={`py-2.5 pl-4 font-medium ${BATCH_STATUS[batch.status].className}`}>{BATCH_STATUS[batch.status].label}</td>
                  <td className="py-2.5 text-right">
                    {batch.status !== 'reverted' ? (
                      <a href={`/api/datev/exports/${batch.id}/download`} className="inline-flex items-center gap-1 text-pipedrive-blue hover:underline">
                        <Download className="h-3.5 w-3.5" />
                        ZIP
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </SettingsCard>
  );
}

function MappingCard({ chart, mappings, onChange }: { chart: '03' | '04'; mappings: Mapping[]; onChange: (next: Mapping[]) => void }) {
  const [error, setError] = React.useState<string | null>(null);
  const [savingKey, setSavingKey] = React.useState<string | null>(null);

  const save = async (row: Mapping, account: string | null, buKey: string | null) => {
    const key = `${row.direction}:${row.taxCase}`;
    setSavingKey(key);
    setError(null);
    try {
      const d = await api<{ mappings: Mapping[] }>('/api/settings/datev/mappings', {
        method: 'PUT',
        body: JSON.stringify({ direction: row.direction, tax_case: row.taxCase, account, bu_key: buKey }),
      });
      onChange(d.mappings);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div>
      <ErrorBox text={error} />
      {(['outgoing', 'incoming'] as const).map((direction) => (
        <SettingsGroup
          key={direction}
          title={direction === 'outgoing' ? 'Ausgangsrechnungen' : 'Eingangsrechnungen'}
          description={
            direction === 'outgoing'
              ? `Erlöskonten je Steuerfall. Vorschlag nach SKR${chart} mit Automatikkonten, bitte mit der Kanzlei abstimmen.`
              : 'Aufwandskonten je Steuerfall. Ein Standard-Aufwandskonto am Lieferanten hat Vorrang.'
          }
        >
          <div className="overflow-x-auto px-5 py-2">
            <table className="w-full min-w-[480px] table-fixed text-sm">
              <thead>
                <tr className={`text-left ${SETTINGS_TABLE_HEADER}`}>
                  <th className="py-2 pr-3 font-medium">Steuerfall</th>
                  <th className="w-32 py-2 pr-3 font-medium">Konto</th>
                  <th className="w-28 py-2 pr-3 font-medium">BU</th>
                  <th className="w-40 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {mappings
                  .filter((row) => row.direction === direction)
                  .map((row) => (
                    <MappingRow
                      key={`${row.direction}:${row.taxCase}:${row.account}:${row.buKey ?? ''}`}
                      row={row}
                      saving={savingKey === `${row.direction}:${row.taxCase}`}
                      onSave={(account, buKey) => void save(row, account, buKey)}
                    />
                  ))}
              </tbody>
            </table>
          </div>
        </SettingsGroup>
      ))}
    </div>
  );
}

function MappingRow({ row, saving, onSave }: { row: Mapping; saving: boolean; onSave: (account: string | null, buKey: string | null) => void }) {
  const [account, setAccount] = React.useState(row.account);
  const [buKey, setBuKey] = React.useState(row.buKey ?? '');
  const commit = () => {
    if (account === row.account && buKey === (row.buKey ?? '')) return;
    if (!account.trim()) {
      setAccount(row.account);
      return;
    }
    onSave(account.trim(), buKey.trim() || null);
  };
  return (
    <tr className="border-t border-app-border/60">
      <td className="py-2 pr-3 text-app-text">{row.label}</td>
      <td className="py-2 pr-3">
        <input
          inputMode="numeric"
          aria-label={`Konto ${row.label}`}
          value={account}
          disabled={saving}
          onChange={(e) => setAccount(e.target.value.replace(/[^\d]/g, ''))}
          onBlur={commit}
          className={`${SETTINGS_INPUT_CLASS} w-24 tabular-nums`}
        />
      </td>
      <td className="py-2 pr-3">
        <input
          inputMode="numeric"
          aria-label={`BU-Schlüssel ${row.label}`}
          value={buKey}
          disabled={saving}
          placeholder="—"
          onChange={(e) => setBuKey(e.target.value.replace(/[^\d]/g, '').slice(0, 4))}
          onBlur={commit}
          className={`${SETTINGS_INPUT_CLASS} w-20 tabular-nums`}
        />
      </td>
      <td className="py-2 text-right">
        {row.isDefault ? (
          <SettingsStatusChip tone="muted">Standard</SettingsStatusChip>
        ) : (
          <button type="button" className="text-xs text-app-text-muted hover:text-app-text" disabled={saving} onClick={() => onSave(null, null)}>
            Standard wiederherstellen
          </button>
        )}
      </td>
    </tr>
  );
}

function CostCenterCard({ costCenters, onChange }: { costCenters: CostCenter[]; onChange: (next: CostCenter[]) => void }) {
  const [code, setCode] = React.useState('');
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await api<{ costCenter: CostCenter }>('/api/settings/datev/cost-centers', {
        method: 'POST',
        body: JSON.stringify({ code: code.trim(), name: name.trim() }),
      });
      onChange([...costCenters, d.costCenter].sort((a, b) => a.code.localeCompare(b.code)));
      setCode('');
      setName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (center: CostCenter) => {
    setError(null);
    try {
      const d = await api<{ costCenter: CostCenter }>(`/api/settings/datev/cost-centers/${center.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !center.active }),
      });
      onChange(costCenters.map((item) => (item.id === center.id ? d.costCenter : item)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div>
      <SettingsGroup title="Kostenstellen" description="Gehen als KOST1 an DATEV. Die Projektnummer wird automatisch als KOST2 übergeben.">
        {error ? (
          <div className="px-5 py-3">
            <ErrorBox text={error} />
          </div>
        ) : null}
        {costCenters.length === 0 ? <p className="px-5 py-4 text-sm text-app-text-muted">Noch keine Kostenstellen angelegt.</p> : null}
        {costCenters.map((center) => (
          <div key={center.id} className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0 text-sm">
              <span className="font-medium tabular-nums text-app-text">{center.code}</span>
              {center.name ? <span className="ml-2 text-app-text-muted">{center.name}</span> : null}
            </div>
            <SettingsToggle active={center.active} label={`Kostenstelle ${center.code} aktiv`} onClick={() => void toggle(center)} />
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 bg-app-surface-muted/50 px-5 py-3">
          <input
            aria-label="Nummer der Kostenstelle"
            placeholder="Nummer"
            value={code}
            disabled={busy}
            onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 36))}
            className={`${SETTINGS_INPUT_CLASS} w-28`}
          />
          <input
            aria-label="Bezeichnung der Kostenstelle"
            placeholder="Bezeichnung"
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
            className={`${SETTINGS_INPUT_CLASS} min-w-0 flex-1`}
          />
          <button type="button" className={SETTINGS_BTN_OUTLINE} disabled={busy || !code.trim()} onClick={() => void add()}>
            <Plus className="h-4 w-4" />
            Hinzufügen
          </button>
        </div>
      </SettingsGroup>
    </div>
  );
}
